
import React, { useState } from 'react';
import { Task } from '@/types/task';
import { Card } from '@/components/ui/card';
import { EditTaskDialog } from './EditTaskDialog';
import { TaskHeader } from './TaskCard/TaskHeader';
import { TaskActions } from './TaskCard/TaskActions';
import { CompletionStatus } from './TaskCard/CompletionStatus';
import { useAuth } from '@/contexts/AuthContext';

interface TaskCardProps {
  task: Task;
  isAdmin: boolean;
  onComplete: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onEdit: (taskId: string, data: any) => void;
  onReactivate?: (task: Task) => void;
}

export const TaskCard = ({ 
  task, 
  isAdmin, 
  onComplete, 
  onDelete, 
  onEdit,
  onReactivate
}: TaskCardProps) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { user, profile } = useAuth();
  const isUserAdmin = profile?.role === 'admin';

  const handleEdit = (data: any) => {
    if (!isUserAdmin) return;
    onEdit(task.id, data);
    setShowEditDialog(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Only open edit dialog if the click was directly on the card
    // and not on a child element that has its own click handler
    if (e.target === e.currentTarget || 
        (e.target as HTMLElement).classList.contains('card-content-wrapper')) {
      // Only allow editing for admin users
      if (!isUserAdmin) return;
      setShowEditDialog(true);
    }
  };

  const handleOpenEditDialog = () => {
    if (!isUserAdmin) return;
    setShowEditDialog(true);
  };

  const getTimeString = () => {
    if (!task.scheduledHour && !task.scheduledMinute) return '';
    
    const hour = task.scheduledHour?.toString().padStart(2, '0') || '00';
    const minute = task.scheduledMinute?.toString().padStart(2, '0') || '00';
    return `${hour}:${minute} - `;
  };

  return (
    <>
      <Card 
        className={`p-4 mb-4 transition-all duration-300 w-full relative group ${
          task.isCompleted ? 'bg-cat-success/20' : isUserAdmin ? 'hover:shadow-lg cursor-pointer' : ''
        }`}
        onClick={handleCardClick}
      >
        <div className="card-content-wrapper flex flex-col space-y-3">
          <TaskHeader 
            isAdmin={isUserAdmin} 
            onDelete={onDelete} 
            taskId={task.id}
            task={task}
            onEdit={handleOpenEditDialog}
          />
          
          <h3 className="font-semibold text-left">
            {getTimeString()}{task.title}
          </h3>

          <div>
            {task.isCompleted ? (
              <CompletionStatus 
                isAdmin={isUserAdmin}
                task={task}
                onReactivate={isUserAdmin ? onReactivate : undefined}
              />
            ) : (
              <TaskActions
                task={task}
                onComplete={onComplete}
              />
            )}
          </div>

          {task.description && (
            <p className="text-sm text-gray-500 text-left">{task.description}</p>
          )}
        </div>
      </Card>

      {isUserAdmin && showEditDialog && (
        <EditTaskDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          task={task}
          onSubmit={handleEdit}
        />
      )}
    </>
  );
};
